import React, { useEffect, useMemo, useState } from "react";
import Header from "../general/Header";
import Aside from "../general/Aside";
import Footer from "../general/Footer";
import axios from '../../axios/axiosconf';
import Loading from "../general/Loading";
import Unauthorized from "../general/Unauthorized";
import { Link } from "react-router-dom";
import { useClientPagination } from "./useClientPagination";
import ReportPaginationFooter from "./ReportPaginationFooter";
import {
    getFormaPagoTexto,
    camposProveedorBasicosDesdeLista,
    descargarExcelXlsx,
    descargarCsvReporte,
} from '../../../utils/reporteExportHelpers';

function tienePermiso(userdata) {
    if (userdata.permissions && Array.isArray(userdata.permissions)) {
        return userdata.permissions.some((p) => p.slug === 'ver_listado_documentos_soporte');
    }
    return userdata.up && userdata.up.includes('ver_listado_documentos_soporte');
}

function ReporteDocumentosSoporte() {
    const [loggedIn, setLoggedIn] = useState(false);
    const [hasPermission, setHasPermission] = useState(false);
    const [client, setClient] = useState([]);
    const [loading, setLoading] = useState(false);
    const [documentos, setDocumentos] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [cuentasPorPagar, setCuentasPorPagar] = useState([]);
    const [sedes, setSedes] = useState([]);

    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [usuarioFiltro, setUsuarioFiltro] = useState('');
    const [busquedaProveedor, setBusquedaProveedor] = useState('');
    const [estadoFiltro, setEstadoFiltro] = useState('');
    const [sedeFiltro, setSedeFiltro] = useState('');
    const [historicoCompleto, setHistoricoCompleto] = useState(false);

    useEffect(() => {
        if (!localStorage.getItem('userdata')) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }
        const userdata = JSON.parse(localStorage.getItem('userdata'));
        if (!tienePermiso(userdata)) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }
        setHasPermission(true);

        const hoy = new Date();
        const inicioSemana = new Date(hoy);
        inicioSemana.setDate(hoy.getDate() - hoy.getDay());
        const finSemana = new Date(inicioSemana);
        finSemana.setDate(inicioSemana.getDate() + 6);
        setFechaInicio(inicioSemana.toISOString().split('T')[0]);
        setFechaFin(finSemana.toISOString().split('T')[0]);

        axios.get('/get-client?uid=' + userdata.user_id)
            .then((r) => { if (r.status === 200) setClient(r.data); setLoggedIn(true); })
            .catch(() => setLoggedIn(true));

        axios.get('/users-by-client?uid=' + userdata.user_id)
            .then((r) => { if (r.status === 200) setUsuarios(r.data); })
            .catch(() => setUsuarios([]));

        axios.get('/proveedores?uid=' + userdata.user_id)
            .then((r) => { if (r.status === 200) setProveedores(r.data); })
            .catch(() => setProveedores([]));

        axios.get('/sedes?uid=' + userdata.user_id)
            .then((r) => { if (r.status === 200) setSedes(r.data); })
            .catch(() => setSedes([]));

        axios.get('/cuentas-por-pagar')
            .then((r) => { if (r.status === 200) setCuentasPorPagar(r.data); })
            .catch(() => setCuentasPorPagar([]));
    }, []);

    useEffect(() => {
        if (loggedIn && (historicoCompleto || (fechaInicio && fechaFin))) {
            cargarReporte();
        }
    }, [loggedIn, historicoCompleto, fechaInicio, fechaFin, usuarioFiltro, estadoFiltro, sedeFiltro, busquedaProveedor]);

    const cargarReporte = () => {
        if (!historicoCompleto && (!fechaInicio || !fechaFin)) {
            alert('Seleccione fecha inicio y fecha fin, o active "Histórico completo".');
            return;
        }
        setLoading(true);
        const userdata = JSON.parse(localStorage.getItem('userdata'));
        const params = new URLSearchParams({
            uid: userdata.user_id,
            fecha_inicio: historicoCompleto ? '1990-01-01' : fechaInicio,
            fecha_fin: historicoCompleto ? '3000-12-31' : fechaFin,
        });
        if (usuarioFiltro) params.append('usuario_id', usuarioFiltro);
        if (busquedaProveedor) params.append('busqueda_proveedor', busquedaProveedor);
        if (estadoFiltro !== '') params.append('estado', estadoFiltro);
        if (sedeFiltro) params.append('sede_id', sedeFiltro);

        axios.get('/documentos-soporte?' + params.toString())
            .then((response) => {
                setDocumentos(Array.isArray(response.data) ? response.data : []);
                return axios.get('/cuentas-por-pagar');
            })
            .then((r) => {
                if (r && r.status === 200) setCuentasPorPagar(r.data);
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setDocumentos([]);
                setLoading(false);
            });
    };

    const handleLimpiarFiltros = () => {
        const hoy = new Date();
        const inicioSemana = new Date(hoy);
        inicioSemana.setDate(hoy.getDate() - hoy.getDay());
        const finSemana = new Date(inicioSemana);
        finSemana.setDate(inicioSemana.getDate() + 6);
        setFechaInicio(inicioSemana.toISOString().split('T')[0]);
        setFechaFin(finSemana.toISOString().split('T')[0]);
        setUsuarioFiltro('');
        setBusquedaProveedor('');
        setEstadoFiltro('');
        setSedeFiltro('');
        setHistoricoCompleto(false);
        setTimeout(() => cargarReporte(), 100);
    };

    const getNombreProveedor = (proveedorId) => {
        const p = proveedores.find((x) => x.id === proveedorId);
        return p ? p.nombre_proveedor : 'N/A';
    };

    const getNombreUsuario = (usuarioId) => {
        const u = usuarios.find((x) => x.id === usuarioId);
        return u ? u.name : 'N/A';
    };

    const getSaldoPendiente = (documentoId) => {
        const cuenta = cuentasPorPagar.find((c) => c.documento_soporte_id === documentoId);
        return cuenta ? parseFloat(cuenta.valor_pendiente || 0) : 0;
    };

    const getEstadoTexto = (estado) => {
        switch (parseInt(estado, 10)) {
            case 0: return 'Generado';
            case 1: return 'Enviado DIAN';
            case 2: return 'Anulado';
            case 3: return 'Error DIAN';
            default: return 'Desconocido';
        }
    };

    const getTipoPagoTexto = (documento) => {
        if (parseInt(documento.estado, 10) === 2) return 'No aplica';
        return documento.tipo_pago === 'por_pagar' ? 'Por Pagar' : 'Pagada';
    };

    const getSaldoTexto = (documento) => {
        if (parseInt(documento.estado, 10) === 2) return 0;
        if (documento.tipo_pago === 'por_pagar') return getSaldoPendiente(documento.id);
        return 0;
    };

    const totalesResumen = useMemo(() => {
        let valor = 0;
        let pendiente = 0;
        let enviadosDian = 0;
        documentos.forEach((d) => {
            valor += parseFloat(d.valor_total || 0);
            if (parseInt(d.estado, 10) !== 2 && d.tipo_pago === 'por_pagar') {
                pendiente += getSaldoPendiente(d.id);
            }
            if (parseInt(d.estado, 10) === 1) enviadosDian++;
        });
        return { conteo: documentos.length, valor, pendiente, enviadosDian };
    }, [documentos, cuentasPorPagar]);

    const pagination = useClientPagination(documentos);

    const formatearMoneda = (valor) =>
        '$' + Number(valor || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const filasExcel = () => documentos.map((d) => {
        const prov = camposProveedorBasicosDesdeLista(proveedores, d.proveedor_id);
        return {
            'N° Documento': `${d.prefijo || ''}-${d.numero || ''}`,
            'Fecha': d.fecha_emision ? new Date(d.fecha_emision).toLocaleDateString('es-CO') : '',
            'Proveedor': getNombreProveedor(d.proveedor_id),
            'Tipo doc. proveedor': prov.tipoDocumento,
            'N° doc. proveedor': prov.numeroDocumento,
            'Usuario': getNombreUsuario(d.users_id),
            'Forma de pago': getFormaPagoTexto(d.forma_pago),
            'Valor total': parseFloat(d.valor_total || 0),
            'Estado': getEstadoTexto(d.estado),
            'Estado pago': getTipoPagoTexto(d),
        };
    });

    const exportarExcel = () => {
        if (documentos.length === 0) {
            alert('No hay datos para exportar');
            return;
        }
        descargarExcelXlsx(
            filasExcel(),
            'DocumentosSoporte',
            `reporte_documentos_soporte_${new Date().toISOString().split('T')[0]}.xlsx`
        );
    };

    const exportarCSV = () => {
        if (documentos.length === 0) {
            alert('No hay datos para exportar');
            return;
        }
        descargarCsvReporte(
            filasExcel(),
            `reporte_documentos_soporte_${new Date().toISOString().split('T')[0]}.csv`
        );
    };

    if (hasPermission === false) return <Unauthorized />;
    if (!loggedIn) return <Loading />;

    return (
        <div className="hold-transition" style={{ fontSize: '13px' }}>
            <Header client={client} />
            <Aside client={client} activelink="reports" />
            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-12">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas">Reportes</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas-contables">Contables</Link></li>
                                    <li className="breadcrumb-item active">Documentos soporte</li>
                                </ol>
                            </div>
                        </div>
                        <h1 className="m-0">Reporte de Documentos Soporte</h1>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        <div className="card report-fin-card">
                            <div className="card-header"><h3 className="card-title">Filtros de búsqueda</h3></div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-2">
                                        <label>Fecha inicio</label>
                                        <input type="date" className="form-control form-control-sm" value={fechaInicio} disabled={historicoCompleto} onChange={(e) => setFechaInicio(e.target.value)} />
                                    </div>
                                    <div className="col-md-2">
                                        <label>Fecha fin</label>
                                        <input type="date" className="form-control form-control-sm" value={fechaFin} disabled={historicoCompleto} onChange={(e) => setFechaFin(e.target.value)} />
                                    </div>
                                    <div className="col-md-2">
                                        <label>Usuario</label>
                                        <select className="form-control form-control-sm" value={usuarioFiltro} onChange={(e) => setUsuarioFiltro(e.target.value)}>
                                            <option value="">Todos</option>
                                            {usuarios.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <label>Estado DIAN</label>
                                        <select className="form-control form-control-sm" value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)}>
                                            <option value="">Todos</option>
                                            <option value="0">Generado</option>
                                            <option value="1">Enviado DIAN</option>
                                            <option value="2">Anulado</option>
                                            <option value="3">Error DIAN</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <label>Sede</label>
                                        <select className="form-control form-control-sm" value={sedeFiltro} onChange={(e) => setSedeFiltro(e.target.value)}>
                                            <option value="">Todas</option>
                                            {sedes.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <label>Proveedor</label>
                                        <input type="text" className="form-control form-control-sm" placeholder="Buscar..." value={busquedaProveedor} onChange={(e) => setBusquedaProveedor(e.target.value)} />
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-4">
                                        <div className="form-check mt-1">
                                            <input className="form-check-input" type="checkbox" id="historicoDocSoporte" checked={historicoCompleto} onChange={(e) => setHistoricoCompleto(e.target.checked)} />
                                            <label className="form-check-label" htmlFor="historicoDocSoporte">Histórico completo (sin límite de fechas)</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-3">
                                    <div className="col-md-12">
                                        <button type="button" className="btn btn-primary btn-sm mr-2" onClick={cargarReporte} disabled={loading}>
                                            <i className="fas fa-search"></i> Buscar
                                            {loading && <span className="spinner-border spinner-border-sm ml-2"></span>}
                                        </button>
                                        <button type="button" className="btn btn-secondary btn-sm mr-2" onClick={handleLimpiarFiltros}>
                                            <i className="fas fa-eraser"></i> Limpiar filtros
                                        </button>
                                        <button type="button" className="btn btn-success btn-sm mr-2" onClick={exportarExcel} disabled={documentos.length === 0}>
                                            <i className="fas fa-file-excel"></i> Excel
                                        </button>
                                        <button type="button" className="btn btn-info btn-sm" onClick={exportarCSV} disabled={documentos.length === 0}>
                                            <i className="fas fa-file-csv"></i> CSV
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-3">
                                <div className="card card-outline card-secondary">
                                    <div className="card-body">
                                        <h3 className="mb-0">{totalesResumen.conteo}</h3>
                                        <p className="text-muted mb-0 small">Documentos</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-info">
                                    <div className="card-body">
                                        <h3 className="mb-0">{formatearMoneda(totalesResumen.valor)}</h3>
                                        <p className="text-muted mb-0 small">Valor total</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-warning">
                                    <div className="card-body">
                                        <h3 className="mb-0">{formatearMoneda(totalesResumen.pendiente)}</h3>
                                        <p className="text-muted mb-0 small">Por pagar</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-success">
                                    <div className="card-body">
                                        <h3 className="mb-0">{totalesResumen.enviadosDian}</h3>
                                        <p className="text-muted mb-0 small">Enviados DIAN</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card report-fin-card">
                            <div className="card-header">
                                <h3 className="card-title">Resultados ({documentos.length} documentos)</h3>
                            </div>
                            <div className="card-body p-0">
                                {loading ? (
                                    <div className="text-center py-4"><div className="spinner-border" role="status"><span className="sr-only">Cargando...</span></div></div>
                                ) : (
                                    <div className="report-fin-table-wrap report-fin-table-wrap--fit">
                                        <table className="table table-bordered table-hover mb-0 report-fin-table">
                                            <thead>
                                                <tr>
                                                    <th>N° Documento</th>
                                                    <th>Fecha</th>
                                                    <th>Proveedor</th>
                                                    <th>Usuario</th>
                                                    <th className="text-right">Total</th>
                                                    <th className="text-right">Saldo pendiente</th>
                                                    <th className="text-center">Estado pago</th>
                                                    <th className="text-center">Estado DIAN</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {documentos.length > 0 ? pagination.paginatedItems.map((d) => (
                                                    <tr key={d.id}>
                                                        <td><strong>{d.prefijo}-{d.numero}</strong></td>
                                                        <td>{d.fecha_emision ? new Date(d.fecha_emision).toLocaleDateString('es-CO') : ''}</td>
                                                        <td>{getNombreProveedor(d.proveedor_id)}</td>
                                                        <td>{getNombreUsuario(d.users_id)}</td>
                                                        <td className="text-right"><strong>{formatearMoneda(d.valor_total)}</strong></td>
                                                        <td className="text-right"><strong>{formatearMoneda(getSaldoTexto(d))}</strong></td>
                                                        <td className="text-center">{getTipoPagoTexto(d)}</td>
                                                        <td className="text-center">{getEstadoTexto(d.estado)}</td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-4 text-muted">No se encontraron documentos soporte</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                {!loading && (
                                    <ReportPaginationFooter
                                        totalRegistros={pagination.totalRegistros}
                                        indicePrimerRegistro={pagination.indicePrimerRegistro}
                                        indiceUltimoRegistro={pagination.indiceUltimoRegistro}
                                        paginaActual={pagination.paginaActual}
                                        cambiarPagina={pagination.cambiarPagina}
                                        totalPaginas={pagination.totalPaginas}
                                        registrosPorPagina={pagination.registrosPorPagina}
                                        onPageSizeChange={pagination.handlePageSizeChange}
                                        itemLabel="documentos"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <Footer showToast={false} delay={0} />
        </div>
    );
}

export default ReporteDocumentosSoporte;
